import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { ApiError, api } from '@/lib/api';
import { type OnboardingDbRequest, OnboardingDbRequestSchema } from '@innermate/shared';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

interface Props {
  onAdvance: () => void;
}

type Driver = 'postgres' | 'oracle';

interface SystemForm {
  host: string;
  port: string;
  database: string;
  username: string;
  password: string;
}

interface OracleConnectForm {
  serviceName: string;
  sid: string;
  thickMode: boolean;
  clientLibDir: string;
}

interface ExcelConnectForm {
  host: string;
  port: string;
  database: string;
  username: string;
  password: string;
}

export function StepDb({ onAdvance }: Props) {
  const [driver, setDriver] = useState<Driver>('postgres');
  const [form, setForm] = useState<SystemForm>({
    host: 'localhost',
    port: '4235',
    database: 'innermate',
    username: 'innermate',
    password: '',
  });
  const [oracleConnect, setOracleConnect] = useState<OracleConnectForm>({
    serviceName: '',
    sid: '',
    thickMode: false,
    clientLibDir: '',
  });
  const [excelConnect, setExcelConnect] = useState<ExcelConnectForm>({
    host: 'localhost',
    port: '1521',
    database: '',
    username: '',
    password: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [existingDatabase, setExistingDatabase] = useState(false);

  const mutation = useMutation({
    mutationFn: (body: OnboardingDbRequest) => api.onboardingDb(body),
    onSuccess: (res) => {
      if (res.existingDatabase) {
        setExistingDatabase(true);
        setTimeout(() => onAdvance(), 2500);
      } else {
        onAdvance();
      }
    },
    onError: (err: unknown) => {
      setServerError(err instanceof ApiError ? err.message : '알 수 없는 오류');
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);
    setErrors({});

    let body: Record<string, unknown>;
    if (driver === 'postgres') {
      body = {
        driver: 'postgres',
        host: form.host,
        port: form.port,
        database: form.database,
        username: form.username,
        password: form.password,
      };
    } else {
      const oc: Record<string, unknown> = { thickMode: oracleConnect.thickMode };
      if (oracleConnect.serviceName.trim()) oc.serviceName = oracleConnect.serviceName.trim();
      if (oracleConnect.sid.trim()) oc.sid = oracleConnect.sid.trim();
      if (oracleConnect.clientLibDir.trim()) oc.clientLibDir = oracleConnect.clientLibDir.trim();
      body = {
        driver: 'oracle',
        host: form.host,
        port: form.port,
        database: form.database,
        username: form.username,
        password: form.password,
        oracleConnect: oc,
        excelConnect: {
          host: excelConnect.host,
          port: excelConnect.port,
          database: excelConnect.database,
          username: excelConnect.username,
          password: excelConnect.password,
        },
      };
    }

    const parsed = OnboardingDbRequestSchema.safeParse(body);
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        fieldErrors[issue.path.join('.')] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    mutation.mutate(parsed.data);
  };

  return (
    <form onSubmit={submit} className="space-y-5">
      <div className="space-y-1.5">
        <span className="text-sm font-medium text-foreground">데이터베이스 종류</span>
        <div className="inline-flex rounded-md border border-border bg-muted p-0.5">
          <Button
            type="button"
            variant={driver === 'postgres' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setDriver('postgres')}
            aria-pressed={driver === 'postgres'}
          >
            PostgreSQL
          </Button>
          <Button
            type="button"
            variant={driver === 'oracle' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setDriver('oracle')}
            aria-pressed={driver === 'oracle'}
          >
            Oracle
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2">
          <Field label="호스트" htmlFor="host" error={errors.host}>
            <Input
              id="host"
              value={form.host}
              onChange={(e) => setForm({ ...form, host: e.target.value })}
              autoComplete="off"
            />
          </Field>
        </div>
        <Field label="포트" htmlFor="port" error={errors.port}>
          <Input
            id="port"
            value={form.port}
            onChange={(e) => setForm({ ...form, port: e.target.value })}
            inputMode="numeric"
          />
        </Field>
      </div>

      <Field label="데이터베이스" htmlFor="database" error={errors.database}>
        <Input
          id="database"
          value={form.database}
          onChange={(e) => setForm({ ...form, database: e.target.value })}
          autoComplete="off"
        />
      </Field>

      <Field label="사용자" htmlFor="username" error={errors.username}>
        <Input
          id="username"
          value={form.username}
          onChange={(e) => setForm({ ...form, username: e.target.value })}
          autoComplete="off"
        />
      </Field>

      <Field
        label="비밀번호"
        htmlFor="password"
        hint="docker-compose 기본값은 change-me-locally"
        error={errors.password}
      >
        <Input
          id="password"
          type="password"
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          autoComplete="new-password"
        />
      </Field>

      {driver === 'oracle' ? (
        <>
          <div className="space-y-4 rounded-md border border-border bg-muted/30 p-4">
            <p className="text-sm font-medium text-foreground">Oracle 접속 옵션</p>

            <Field
              label="서비스명 (serviceName)"
              htmlFor="oracle-serviceName"
              hint="serviceName 또는 SID 중 하나"
              error={errors['oracleConnect.serviceName']}
            >
              <Input
                id="oracle-serviceName"
                value={oracleConnect.serviceName}
                onChange={(e) =>
                  setOracleConnect({ ...oracleConnect, serviceName: e.target.value })
                }
                autoComplete="off"
                placeholder="예: ORCLPDB1"
              />
            </Field>

            <Field
              label="SID (선택)"
              htmlFor="oracle-sid"
              hint="serviceName 을 쓰지 않을 때만 입력"
              error={errors['oracleConnect.sid']}
            >
              <Input
                id="oracle-sid"
                value={oracleConnect.sid}
                onChange={(e) => setOracleConnect({ ...oracleConnect, sid: e.target.value })}
                autoComplete="off"
              />
            </Field>

            <div className="space-y-1.5">
              <label
                htmlFor="oracle-thickMode"
                className="flex items-start gap-2.5 text-sm text-foreground"
              >
                <input
                  id="oracle-thickMode"
                  type="checkbox"
                  checked={oracleConnect.thickMode}
                  onChange={(e) =>
                    setOracleConnect({ ...oracleConnect, thickMode: e.target.checked })
                  }
                  className="mt-0.5 size-4 rounded border-border accent-accent"
                />
                <span>
                  Thick 모드 사용
                  <span className="block text-xs text-foreground-muted">
                    Oracle Instant Client 가 설치된 경우에만 활성화. 미설치 시 끄세요.
                  </span>
                </span>
              </label>
            </div>

            {oracleConnect.thickMode ? (
              <Field
                label="Instant Client 경로 (clientLibDir)"
                htmlFor="oracle-clientLibDir"
                hint="Thick 모드용 라이브러리 디렉터리 (선택)"
                error={errors['oracleConnect.clientLibDir']}
              >
                <Input
                  id="oracle-clientLibDir"
                  value={oracleConnect.clientLibDir}
                  onChange={(e) =>
                    setOracleConnect({ ...oracleConnect, clientLibDir: e.target.value })
                  }
                  autoComplete="off"
                  placeholder="예: /opt/oracle/instantclient_21_12"
                />
              </Field>
            ) : null}
          </div>

          <div className="space-y-4 rounded-md border border-border bg-muted/30 p-4">
            <div>
              <p className="text-sm font-medium text-foreground">엑셀 격리 전용 접속</p>
              <p className="text-xs text-foreground-muted">
                엑셀 첨부 데이터를 시스템 DB 와 분리해 보관할 별도 Oracle 접속입니다. Oracle 사용 시
                필수.
              </p>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <Field label="호스트" htmlFor="excel-host" error={errors['excelConnect.host']}>
                  <Input
                    id="excel-host"
                    value={excelConnect.host}
                    onChange={(e) => setExcelConnect({ ...excelConnect, host: e.target.value })}
                    autoComplete="off"
                  />
                </Field>
              </div>
              <Field label="포트" htmlFor="excel-port" error={errors['excelConnect.port']}>
                <Input
                  id="excel-port"
                  value={excelConnect.port}
                  onChange={(e) => setExcelConnect({ ...excelConnect, port: e.target.value })}
                  inputMode="numeric"
                />
              </Field>
            </div>

            <Field
              label="데이터베이스"
              htmlFor="excel-database"
              error={errors['excelConnect.database']}
            >
              <Input
                id="excel-database"
                value={excelConnect.database}
                onChange={(e) => setExcelConnect({ ...excelConnect, database: e.target.value })}
                autoComplete="off"
              />
            </Field>

            <Field label="사용자" htmlFor="excel-username" error={errors['excelConnect.username']}>
              <Input
                id="excel-username"
                value={excelConnect.username}
                onChange={(e) => setExcelConnect({ ...excelConnect, username: e.target.value })}
                autoComplete="off"
              />
            </Field>

            <Field
              label="비밀번호"
              htmlFor="excel-password"
              error={errors['excelConnect.password']}
            >
              <Input
                id="excel-password"
                type="password"
                value={excelConnect.password}
                onChange={(e) => setExcelConnect({ ...excelConnect, password: e.target.value })}
                autoComplete="new-password"
              />
            </Field>
          </div>
        </>
      ) : null}

      {serverError ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive whitespace-pre-line">
          {serverError}
        </div>
      ) : null}

      {existingDatabase ? (
        <div className="rounded-md border border-[hsl(200_80%_50%)]/30 bg-[hsl(200_80%_50%)]/5 px-3 py-2 text-sm text-[hsl(200_80%_50%)]">
          기존에 초기화된 데이터베이스가 감지되었습니다. MinIO 설정만 완료하면 바로 로그인 화면으로
          이동합니다.
        </div>
      ) : null}

      <div className="flex justify-end pt-2">
        <Button type="submit" disabled={mutation.isPending || existingDatabase}>
          {mutation.isPending ? '연결 테스트 중…' : '연결 테스트 후 다음'}
        </Button>
      </div>
    </form>
  );
}
